import { loadDashboardCharts } from '../utils/loadDashboardCharts';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import api from '../utils/apiClient';
import { Container, Typography, Switch, FormControlLabel, Button, Divider, Chip, TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, Box, Grid, Checkbox, List, ListItem, ListItemText, ListItemIcon, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Select, MenuItem } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Menu from '../components/Menu';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Workers from '../components/dashboard/Workers';
import UptimeSum from '../components/dashboard/UptimeSum';
import Graph from '../components/dashboard/Graph';
import Hashrate from '../components/dashboard/Hashrate';
import SummaryPanel from '../components/dashboard/SummaryPanel';
import LocationTree from '../components/dashboard/LocationTree';
import ViewSwitcher from '../components/dashboard/ViewSwitcher';
import DeviceTable from '../components/dashboard/DeviceTable';
import AgentControl from '../components/AgentControl';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Commands from '../components/commands';
import { useNotifications } from '../context/NotificationContext';
import NotificationContainer from '../components/NotificationContainer';
import MoveDevicesDialog from '../components/MoveDevicesDialog';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { isHosterOrAbove, isAdminOrSuperAdmin } from '../utils/roles';
import useRefetchOnHostingChange from '../hooks/useRefetchOnHostingChange';

const ResponsiveGridLayout = WidthProvider(Responsive);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_DASHBOARD_URL = process.env.REACT_APP_API_DASHBOARD_URL;

// Константы для кеширования
const CACHE_KEY = 'dashboard_devices_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 час в миллисекундах

// Утилиты для работы с кешем устройств
const deviceCache = {
  // Получить данные из кеша
  get: () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Проверяем TTL
      if (now - timestamp > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      return data;
    } catch (error) {
      return null;
    }
  },
  
  // Сохранить данные в кеш
  set: (devices) => {
    try {
      // Фильтруем устройства: не кешируем устройства со статусом "removed"
      const devicesToCache = devices.filter(device => {
        const status = device.status?.toLowerCase();
        return status !== 'removed';
      });

      const cacheData = {
        data: devicesToCache.map(device => ({
          id: device.id,
          model: device.model,
          algorithm: device.algorithm,
          mac: device.mac,
          ip_address: device.ip_address || device.ip,
          serial_number: device.serial_number,
          tags: device.tags,
          // Worker не кешируется отдельно, т.к. вычисляется из pools
          owner_id: device.owner_id,
          owner_name: device.owner_name,
          hosting_id: device.hosting_id,
          hosting_name: device.hosting_name,
          pools: device.pools, // Pools кешируются для вычисления worker
          location: device.location,
          hashrate_unit: device.hashrate_unit,
        })),
        timestamp: Date.now(),
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      // Ошибка сохранения кеша
    }
  },
  
  // Очистить кеш
  clear: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      // Ошибка очистки кеша
    }
  },
  
  // Синхронизация кеша с новыми данными API
  sync: (apiDevices) => {
    try {
      const cached = deviceCache.get();
      if (!cached) {
        deviceCache.set(apiDevices);
        return apiDevices;
      }
      
      // Создаем Map для быстрого доступа по ID
      const cachedMap = new Map(cached.map(d => [d.id, d]));
      const apiDevicesMap = new Map(apiDevices.map(d => [d.id, d]));
      
      // Удаляем из кеша устройства, которых нет в API
      const validDevices = cached.filter(d => apiDevicesMap.has(d.id));
      
      // Обновляем существующие и добавляем новые устройства
      const syncedDevices = apiDevices.map(apiDevice => {
        const cachedDevice = cachedMap.get(apiDevice.id);
        return {
          ...apiDevice,
          // Статичные данные берем из кеша, если есть, иначе из API
          model: apiDevice.model ?? cachedDevice?.model,
          algorithm: apiDevice.algorithm ?? cachedDevice?.algorithm,
          mac: apiDevice.mac ?? cachedDevice?.mac,
          ip_address: (apiDevice.ip_address || apiDevice.ip) ?? cachedDevice?.ip_address,
          serial_number: apiDevice.serial_number ?? cachedDevice?.serial_number,
          tags: apiDevice.tags ?? cachedDevice?.tags,
          owner_id: apiDevice.owner_id ?? cachedDevice?.owner_id,
          owner_name: apiDevice.owner_name ?? cachedDevice?.owner_name,
          hosting_id: apiDevice.hosting_id ?? cachedDevice?.hosting_id,
          hosting_name: apiDevice.hosting_name ?? cachedDevice?.hosting_name,
          pools: apiDevice.pools ?? cachedDevice?.pools,
          location: apiDevice.location ?? cachedDevice?.location,
          hashrate_unit: apiDevice.hashrate_unit ?? cachedDevice?.hashrate_unit,
        };
      });
      
      // Сохраняем синхронизированные данные в кеш
      deviceCache.set(syncedDevices);
      return syncedDevices;
    } catch (error) {
      // Ошибка синхронизации кеша
      return apiDevices;
    }
  },
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');
  const { showSuccess, showWarning, showInfo, showError } = useNotifications();
  const [isEditing, setIsEditing] = useState(false);
  const [baseLayout, setBaseLayout] = useState([
    { w: 3, h: 6, x: 3, y: 0, i: 'workers' },
    { w: 3, h: 6, x: 0, y: 0, i: 'uptime' },
    { w: 6, h: 6, x: 6, y: 0, i: 'graph' },
    { w: 12, h: 4, x: 0, y: 6, i: 'hashrate' },
  ]);
  const [selectedDeviceIds, setSelectedDeviceIds] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedAlgorithms, setSelectedAlgorithms] = React.useState([]);
  const [selectedHosting, setSelectedHosting] = React.useState([]);
  const [selectedStatus, setSelectedStatus] = React.useState([]);
  const [showArchived, setShowArchived] = React.useState(false);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [openDialog, setOpenDialog] = React.useState(false);
  // Дефолтный порядок колонок таблицы
  const defaultColumnOrder = ['modelName', 'ip', 'status', 'hashrate', 'temperature', 'uptime', 'worker', 'client', 'fan', 'model', 'algorithm', 'mac', 'serialNumber', 'tags'];

  const [visibleColumns, setVisibleColumns] = React.useState({
    // Новый набор по умолчанию (настройки в dashboard-table-v2): остальные — через «Колонки»
    model: false,
    modelName: true,
    status: true,
    algorithm: false,
    mac: false,
    ip: true,
    serialNumber: false,
    tags: false,
    hashrate: true,
    temperature: true,
    fan: false,
    worker: true,
    client: true,
    uptime: true,
    hashrateGraph: true, // График хешрейта
  });

  // Порядок колонок в таблице
  const [columnOrder, setColumnOrder] = React.useState(defaultColumnOrder);

  // Состояние для drag-and-drop колонок
  const [draggedColumn, setDraggedColumn] = React.useState(null);
  const [dragOverColumn, setDragOverColumn] = React.useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dataDashboard, setDataDashboard] = useState(null);
  const [chartError, setChartError] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const chartRequest = useRef(0);
  const [locationsData, setLocationsData] = useState([]);
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60000); // 1 минута по умолчанию
  // TODO: Вернуть состояние выбора типа отображения после восстановления UI
  // const [displayType, setDisplayType] = useState('asic'); // Состояние для выпадающего меню
  const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);
  // Период: пресет запоминается между входами (свой диапазон — нет, он привязан к датам)
  const PERIOD_PRESETS = { '1h': [1, 'hour'], '24h': [24, 'hour'], '3d': [3, 'day'], '30d': [30, 'day'] };
  const [periodPreset, setPeriodPreset] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboard-period');
      return PERIOD_PRESETS[saved] ? saved : '24h';
    } catch (e) {
      return '24h';
    }
  });
  const [dateRange, setDateRange] = useState(() => {
    const [n, unit] = PERIOD_PRESETS[periodPreset] || PERIOD_PRESETS['24h'];
    return { start: dayjs().subtract(n, unit), end: dayjs() };
  });
  const [rangeNonce, setRangeNonce] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [workSummary, setWorkSummary] = useState(null);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [isTreeView, setIsTreeView] = useState(false);
  const [expandedFilter, setExpandedFilter] = React.useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'ip', direction: 'asc' });
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 100 }); // По умолчанию 100 устройств
  const [isFiltersLoaded, setIsFiltersLoaded] = useState(false); // Флаг загрузки фильтров из localStorage

  // Вычисляемые layouts для разных breakpoints
  const layouts = useMemo(() => {
    let lgLayout;
    if (visibleColumns.hashrateGraph) {
      lgLayout = baseLayout;
    } else {
      lgLayout = baseLayout.map(item => {
        if (item.i === 'workers') return { ...item, w: 6, x: 0 };
        if (item.i === 'uptime') return { ...item, w: 6, x: 6 };
        if (item.i === 'graph') return { ...item, h: 0, w: 0 };
        if (item.i === 'hashrate') return { ...item, y: 6 };
        return item;
      });
    }

    // Мобильный layout - все виджеты в 1 колонку, без graph если скрыт
    const smLayout = visibleColumns.hashrateGraph
      ? [
          { w: 1, h: 6, x: 0, y: 0, i: 'uptime' },
          { w: 1, h: 6, x: 0, y: 6, i: 'workers' },
          { w: 1, h: 8, x: 0, y: 12, i: 'graph' },
          { w: 1, h: 5, x: 0, y: 20, i: 'hashrate' },
        ]
      : [
          { w: 1, h: 6, x: 0, y: 0, i: 'uptime' },
          { w: 1, h: 6, x: 0, y: 6, i: 'workers' },
          { w: 1, h: 0, x: 0, y: 12, i: 'graph' },
          { w: 1, h: 5, x: 0, y: 12, i: 'hashrate' },
        ];

    return { lg: lgLayout, sm: smLayout, xs: smLayout };
  }, [baseLayout, visibleColumns.hashrateGraph]);

  // Функция для определения периодов с дополнительными статусами (repair, maintenance и т.д.)
  // Эти периоды должны быть исключены из расчета uptime и считаться как offline для хешрейта
  const getExcludedPeriods = (uptimeData, startTime, endTime) => {
    if (!uptimeData || !Array.isArray(uptimeData) || uptimeData.length === 0) {
      return [];
    }

    const excludedPeriods = [];

    uptimeData.forEach((uptimeRecord) => {
      // Дополнительные статусы - все, кроме "online" и "offline"
      if (uptimeRecord.status !== 'online' && uptimeRecord.status !== 'offline') {
        const recordStart = new Date(uptimeRecord.started_at);
        const recordEnd = uptimeRecord.ended_at ? new Date(uptimeRecord.ended_at) : new Date();

        // Проверяем, пересекается ли период с запрашиваемым интервалом
        if (recordEnd >= startTime && recordStart <= endTime) {
          const effectiveStart = recordStart < startTime ? startTime : recordStart;
          const effectiveEnd = recordEnd > endTime ? endTime : recordEnd;

          if (effectiveEnd > effectiveStart) {
            excludedPeriods.push({
              start: effectiveStart,
              end: effectiveEnd,
              status: uptimeRecord.status
            });
          }
        }
      }
    });

    return excludedPeriods;
  };

  // Функция для расчета общей длительности исключенных периодов
  const calculateExcludedDuration = (excludedPeriods) => {
    if (excludedPeriods.length === 0) return 0;

    // Сортируем периоды по времени начала
    const sortedPeriods = [...excludedPeriods].sort((a, b) => a.start - b.start);

    // Объединяем перекрывающиеся периоды
    const mergedPeriods = [];
    let currentPeriod = sortedPeriods[0];

    for (let i = 1; i < sortedPeriods.length; i++) {
      const nextPeriod = sortedPeriods[i];

      if (nextPeriod.start <= currentPeriod.end) {
        // Периоды перекрываются, объединяем их
        currentPeriod.end = new Date(Math.max(currentPeriod.end.getTime(), nextPeriod.end.getTime()));
      } else {
        // Периоды не перекрываются, сохраняем текущий и переходим к следующему
        mergedPeriods.push(currentPeriod);
        currentPeriod = nextPeriod;
      }
    }
    mergedPeriods.push(currentPeriod);

    // Суммируем длительность всех объединенных периодов
    return mergedPeriods.reduce((total, period) => {
      return total + (period.end - period.start);
    }, 0);
  };

  // Функция для сортировки IP адресов
  const compareIPAddresses = (a, b) => {
    const ipToNumber = (ip) => {
      if (!ip || ip === 'N/A') return [0, 0, 0, 0];
      return ip.split('.').map(num => parseInt(num, 10) || 0);
    };
    
    const aParts = ipToNumber(a);
    const bParts = ipToNumber(b);
    
    for (let i = 0; i < 4; i++) {
      if (aParts[i] !== bParts[i]) {
        return aParts[i] - bParts[i];
      }
    }
    return 0;
  };

  // Функция для расчета коэффициента доступности устройства в заданном временном интервале
  const calculateAvailabilityRatio = (uptimeData, startTime, endTime) => {
    if (!uptimeData || !Array.isArray(uptimeData) || uptimeData.length === 0) {
      return 1; // Если нет данных о uptime, считаем устройство доступным
    }

    const start = new Date(startTime);
    const end = new Date(endTime);
    const totalDuration = end - start;

    if (totalDuration <= 0) return 1;

    // Сортируем записи по времени
    const sortedUptimeData = uptimeData.sort((a, b) => {
      const timeA = new Date(a.started_at || a.created_at);
      const timeB = new Date(b.started_at || b.created_at);
      return timeA - timeB;
    });

    let unavailableDuration = 0;

    sortedUptimeData.forEach((uptimeRecord) => {
      const recordStart = new Date(uptimeRecord.started_at);
      const recordEnd = uptimeRecord.ended_at ? new Date(uptimeRecord.ended_at) : new Date();

      // Учитываем как unavailable:
      // 1. Статус offline (для обратной совместимости с boolean false)
      // 2. Статус "offline" (строка)
      // 3. Любой дополнительный статус (repair, maintenance и т.д.) - все кроме "online"
      const isUnavailable = uptimeRecord.status === false ||
                           uptimeRecord.status === 'offline' ||
                           (uptimeRecord.status !== 'online' && uptimeRecord.status !== true);

      if (isUnavailable && recordEnd >= start && recordStart <= end) {
        const effectiveStart = recordStart < start ? start : recordStart;
        const effectiveEnd = recordEnd > end ? end : recordEnd;

        if (effectiveEnd > effectiveStart) {
          unavailableDuration += effectiveEnd - effectiveStart;
        }
      }
    });

    const availabilityRatio = Math.max(0, (totalDuration - unavailableDuration) / totalDuration);
    return availabilityRatio;
  };

  // Функция для расчета uptime отдельного устройства за выбранный период
  const calculateDeviceUptime = (deviceId) => {
    const deviceData = dataDashboard?.find(data => data.device_id === deviceId);
    if (!deviceData || !deviceData.uptime || !Array.isArray(deviceData.uptime)) {
      return 0;
    }

    const now = new Date();
    // Используем переданный временной диапазон или по умолчанию 24 часа
    let startTime, endTime;

    if (dateRange?.start && dateRange?.end) {
      startTime = dateRange.start.toDate ? new Date(dateRange.start.toDate()) : new Date(dateRange.start);
      endTime = dateRange.end.toDate ? new Date(dateRange.end.toDate()) : new Date(dateRange.end);
    } else {
      // По умолчанию 24 часа
      endTime = now;
      startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    // Получаем периоды с дополнительными статусами (repair, maintenance и т.д.)
    const excludedPeriods = getExcludedPeriods(deviceData.uptime, startTime, endTime);
    const excludedDurationMs = calculateExcludedDuration(excludedPeriods);

    // Сортируем записи по времени
    const sortedUptimeData = deviceData.uptime.sort((a, b) => {
      const timeA = new Date(a.created_at || a.started_at);
      const timeB = new Date(b.created_at || b.started_at);
      return timeA - timeB;
    });

    let deviceUptimeMinutes = 0;

    sortedUptimeData.forEach((uptimeRecord) => {
      // Учитываем только статус "online" (true для обратной совместимости, "online" для новой структуры)
      const isOnline = uptimeRecord.status === true || uptimeRecord.status === 'online';

      if (isOnline) {
        const recordStart = new Date(uptimeRecord.started_at);
        const recordEnd = uptimeRecord.ended_at ? new Date(uptimeRecord.ended_at) : now;

        // Если запись началась раньше выбранного периода, берем только часть
        const effectiveStart = recordStart < startTime ? startTime : recordStart;

        // Если запись еще активна, используем текущее время или конец периода
        const effectiveEnd = recordEnd > endTime ? endTime : recordEnd;

        if (effectiveEnd > effectiveStart) {
          const durationMinutes = (effectiveEnd - effectiveStart) / (1000 * 60);
          deviceUptimeMinutes += durationMinutes;
        }
      }
    });

    // Вычисляем процент аптайма за выбранный период, исключая периоды с дополнительными статусами
    const totalMinutesInPeriod = (endTime - startTime) / (1000 * 60);
    const excludedMinutes = excludedDurationMs / (1000 * 60);

    // Если весь период покрыт дополнительными статусами, возвращаем 0
    if (excludedMinutes >= totalMinutesInPeriod) {
      return 0;
    }

    // Вычисляем процент uptime от доступного времени (исключая периоды с дополнительными статусами)
    const availableMinutes = totalMinutesInPeriod - excludedMinutes;
    const uptimePercentage = availableMinutes > 0 ? (deviceUptimeMinutes / availableMinutes) * 100 : 0;

    return Math.round(uptimePercentage * 100) / 100; // Округляем до 2 знаков
  };

  // Функция для получения цвета uptime
  const getUptimeColor = (percentage) => {
    if (percentage >= 90) return '#4caf50'; // Зеленый
    if (percentage >= 70) return '#ff9800'; // Оранжевый
    if (percentage >= 50) return '#2196f3'; // Синий
    return '#f44336'; // Красный
  };

  // Обработчик сортировки
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Функция для конвертации хешрейта в читаемый формат
  const formatHashrate = (hashrate) => {
    if (hashrate === 0) return '0 H/s';
    
    const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s'];
    const base = 1000;
    const exponent = Math.floor(Math.log(hashrate) / Math.log(base));
    const unit = units[Math.min(exponent, units.length - 1)];
    const value = hashrate / Math.pow(base, Math.min(exponent, units.length - 1));
    
    return `${value.toFixed(2)} ${unit}`;
  };

  // Конвертация значения хешрейта в зависимости от алгоритма
  const convertHashrate = (value, unit, algorithm) => {
    if (value == null || isNaN(value)) return { value: 0, unit: 'TH/s' };
    const normalize = (u) => (typeof u === 'string' ? u.trim().toUpperCase() : '');
    let u = normalize(unit);
    // Обработка возможной опечатки "GG/s"
    if (u === 'GG/S') u = 'GH/S';
    
    // Определяем целевую единицу в зависимости от алгоритма
    let targetUnit = 'TH/s';
    if (algorithm === 'Scrypt') {
      targetUnit = 'TH/s';
    } else if (algorithm === 'Equihash') {
      targetUnit = 'kSol/s';
    }
    
    const factors = {
      'H/S': 1 / 1e12,
      'KH/S': 1 / 1e9,
      'MH/S': 1 / 1e6,
      'GH/S': 1 / 1e3,
      'TH/S': 1,
      'PH/S': 1e3,
      'EH/S': 1e6,
    };
    
    // Конвертируем в TH/s сначала
    const factor = factors[u] ?? 1;
    const thValue = Number(value) * factor;
    
    // Затем конвертируем в целевую единицу с проверкой на превышение 1000
    if (targetUnit === 'TH/s') {
      // Для Scrypt и остальных алгоритмов: если меньше 1000 GH/s, показываем в GH/s, иначе в TH/s
      const ghValue = thValue * 1000; // конвертируем TH/s в GH/s
      if (ghValue < 1000) {
        return { value: ghValue, unit: 'GH/s' };
      }
      
      // Если больше или равно 1000 GH/s, показываем в TH/s с автоматическим переводом
      if (thValue >= 1000) {
        const phValue = thValue / 1000;
        if (phValue >= 1000) {
          const ehValue = phValue / 1000;
          return { value: ehValue, unit: 'EH/s' };
        }
        return { value: phValue, unit: 'PH/s' };
      }
      return { value: thValue, unit: 'TH/s' };
    } else if (targetUnit === 'kSol/s') {
      // Для Equihash: 1 TH/s = 1 kSol/s (примерно)
      let kSolValue = thValue;
      if (kSolValue >= 1000) {
        const thFromKSol = kSolValue / 1000;
        if (thFromKSol >= 1000) {
          const phFromTh = thFromKSol / 1000;
          return { value: phFromTh, unit: 'PH/s' };
        }
        return { value: thFromKSol, unit: 'TH/s' };
      }
      return { value: kSolValue, unit: 'kSol/s' };
    } else {
      // Для остальных алгоритмов (TH/s)
      if (thValue >= 1000) {
        const phValue = thValue / 1000;
        if (phValue >= 1000) {
          const ehValue = phValue / 1000;
          return { value: ehValue, unit: 'EH/s' };
        }
        return { value: phValue, unit: 'PH/s' };
      }
      return { value: thValue, unit: 'TH/s' };
    }
  };

  // Обратная совместимость - старая функция для TH/s
  const convertHashrateToTH = (value, unit) => {
    const result = convertHashrate(value, unit, 'SHA-256');
    return result.value;
  };

  // Нормализация статуса из v3 (online/offline/repair и другие)
  const normalizeStatusValue = (status) => {
    // Если статус null или undefined, возвращаем null
    if (status == null) return null;

    // Если это boolean (для обратной совместимости)
    if (status === true) return 'online';
    if (status === false) return 'offline';

    // Если это строка, возвращаем нормализованную строку
    if (typeof status === 'string') {
      const normalized = status.trim().toLowerCase();
      return normalized || null;
    }

    return null;
  };

  // Извлекаем мгновенные метрики устройства из latest_metrics
  const extractInstantDeviceMetricsFromV3 = (device) => {
    if (!device || typeof device !== 'object') {
      return { hashrate: null, temperatures: [], fans: [] };
    }

    const readTemps = (src) => [
      src?.temperature0, src?.temperature1, src?.temperature2,
      src?.temperature3, src?.temperature4, src?.temperature5
    ].filter((v) => v != null);

    const readFans = (src) => [
      src?.fan_speed0, src?.fan_speed1, src?.fan_speed2, src?.fan_speed3
    ].filter((v) => v != null);

    // Приоритет: latest_metrics из нового API
    if (device.latest_metrics) {
      const metrics = device.latest_metrics;
      return {
        hashrate: metrics.hashrate != null ? Number(metrics.hashrate) : null,
        temperatures: readTemps(metrics),
        fans: readFans(metrics)
      };
    }

    // Fallback на старые структуры для обратной совместимости
    const metricCandidate = Array.isArray(device.metrics) && device.metrics.length > 0
      ? device.metrics[device.metrics.length - 1]
      : device.last_metric || device.metric || null;

    const valueSlice = Array.isArray(device.value) && device.value.length > 0
      ? device.value[device.value.length - 1]
      : null;

    let hashrate = null;
    let temperatures = [];
    let fans = [];

    if (metricCandidate) {
      hashrate = metricCandidate.hashrate != null ? Number(metricCandidate.hashrate) : null;
      temperatures = readTemps(metricCandidate);
      fans = readFans(metricCandidate);
    }

    if ((hashrate == null || !isFinite(hashrate)) && valueSlice) {
      const vMax = valueSlice.hashrate_max;
      const vMin = valueSlice.hashrate_min;
      const candidate = vMax != null ? vMax : vMin;
      hashrate = candidate != null ? Number(candidate) : hashrate;
    }

    if (!Array.isArray(temperatures) || temperatures.length === 0) {
      if (valueSlice) {
        const tempValues = [];
        for (let i = 0; i < 6; i++) {
          const maxKey = `temperature${i}_max`;
          const minKey = `temperature${i}_min`;
          const v = valueSlice[maxKey] != null ? valueSlice[maxKey] : valueSlice[minKey];
          if (v != null) tempValues.push(v);
        }
        temperatures = tempValues;
      }
    }

    if (!Array.isArray(fans) || fans.length === 0) {
      if (valueSlice) {
        const fanValues = [];
        for (let i = 0; i < 4; i++) {
          const maxKey = `fan_speed${i}_max`;
          const minKey = `fan_speed${i}_min`;
          const v = valueSlice[maxKey] != null ? valueSlice[maxKey] : valueSlice[minKey];
          if (v != null) fanValues.push(v);
        }
        fans = fanValues;
      }
    }

    if (hashrate == null || !isFinite(hashrate)) {
      const topHashrate = device.hashrate != null ? Number(device.hashrate) : (device.current_hashrate != null ? Number(device.current_hashrate) : null);
      hashrate = isFinite(topHashrate) ? topHashrate : hashrate;
    }
    if (!Array.isArray(temperatures) || temperatures.length === 0) {
      const topTemps = readTemps(device);
      temperatures = Array.isArray(topTemps) ? topTemps : [];
    }
    if (!Array.isArray(fans) || fans.length === 0) {
      const topFans = readFans(device);
      fans = Array.isArray(topFans) ? topFans : [];
    }

    return { hashrate: isFinite(hashrate) ? hashrate : null, temperatures, fans };
  };

  // Функция для загрузки локаций
  const fetchLocations = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}locations`);

      const locations = response.data?.locations || [];
      return locations;
    } catch (error) {
      return [];
    }
  };

  // Функция для загрузки устройств с пагинацией
  const fetchAllDevices = async () => {
    const limit = 1000;
    let offset = 0;
    let allDevices = [];
    let hasMore = true;
    let userData = null;
    let lastUpdateData = null;

    while (hasMore) {
      try {
        const response = await api.get(`${API_DASHBOARD_URL}/device-api/v1/dashboard/devices`, {
          params: {
            limit,
            offset,
            ...(showArchived && { show_archived: 'true' }),
          },
        });

        const devices = response.data?.devices || [];
        const pagination = response.data?.pagination;

        allDevices = [...allDevices, ...devices];

        // Сохраняем user и lastUpdate из первого ответа
        if (offset === 0) {
          userData = response.data?.user;
          lastUpdateData = response.data?.lastUpdate;
        }

        // Проверяем, есть ли еще данные
        if (pagination?.hasMore === false || devices.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      } catch (error) {
        hasMore = false;
      }
    }

    return {
      devices: allDevices,
      user: userData,
      lastUpdate: lastUpdateData,
      isPartial: false,
    };
  };

  // Функция для загрузки графиков с пагинацией
  const fetchAllCharts = (startDate, endDate) => loadDashboardCharts(api, API_DASHBOARD_URL, startDate, endDate);

  // Функция для загрузки данных dashboard
  const fetchDashboardData = useCallback(async (isAutoUpdate = false) => {
    const requestId = ++chartRequest.current;
    setChartLoading(true);
    try {
      // При автообновлении сдвигаем временное окно к текущему времени
      let start, end;
      if (isAutoUpdate && dateRange?.start && dateRange?.end) {
        const now = dayjs();
        const windowDuration = dateRange.end.diff(dateRange.start, 'millisecond');
        end = now;
        start = now.subtract(windowDuration, 'millisecond');

        // Обновляем состояние dateRange для отображения актуального диапазона
        setDateRange({ start, end });
      } else {
        // Диапазон дат из выбора пользователя (последние 24 часа по умолчанию)
        start = dateRange?.start ? dateRange.start : dayjs().subtract(1, 'day');
        end = dateRange?.end ? dateRange.end : dayjs();
      }

      const startDate = start.toDate ? start.toDate() : start;
      const endDate = end.toDate ? end.toDate() : end;

      // Загружаем локации
      const locations = await fetchLocations();
      setLocationsData(locations);

      // Загружаем устройства с пагинацией
      const devicesData = await fetchAllDevices();

      // Синхронизируем устройства с кешем
      if (devicesData?.devices) {
        const syncedDevices = deviceCache.sync(devicesData.devices);
        setDashboardData({
          devices: syncedDevices,
          user: devicesData.user,
          lastUpdate: devicesData.lastUpdate,
        });
      }

      // Сохраняем данные пользователя в localStorage только если их еще нет
      if (devicesData?.user) {
        const existingUserData = localStorage.getItem('userData');
        if (!existingUserData) {
          localStorage.setItem('userData', JSON.stringify({
            id: devicesData.user.id,
            email: devicesData.user.email,
            name: devicesData.user.name,
            role: devicesData.user.role,
          }));
        }
      }

      // Графики грузим всегда: по ним считаются аптайм и график хешрейта в сводке
      try {
        const chartsData = await fetchAllCharts(startDate, endDate);
        if (requestId === chartRequest.current) { setDataDashboard(chartsData); setChartError(false); }
      } catch (itemsError) {
        if (requestId === chartRequest.current) { setDataDashboard([]); setChartError(true); }
      }
      setLastUpdated(new Date());
    } catch (error) {
      if (requestId === chartRequest.current) { setDataDashboard([]); setChartError(true); }
    } finally {
      if (requestId === chartRequest.current) setChartLoading(false);
    }
  }, [dateRange, showArchived]);

  const createDeviceData = (device, dashboardData = []) => {
    if (!dashboardData || !Array.isArray(dashboardData)) {
      const status = normalizeStatusValue(device?.status);
      const unit = device?.hashrate_unit || device?.hashrateUnit || 'TH/s';
      const { hashrate: v3Hashrate, temperatures: v3Temps, fans: v3Fans } = extractInstantDeviceMetricsFromV3(device);
      let hashrateDisplay = '-';
      // Показываем хешрейт только для устройств со статусом Online
      if (status === 'online' && v3Hashrate != null) {
        const result = convertHashrate(v3Hashrate, unit, device.algorithm);
        hashrateDisplay = `${result.value.toFixed(2)} ${result.unit}`;
      }
      return {
        model: device.model,
        modelName: device.model_name || null,
        status,
        algorithm: device.algorithm,
        mac: device.mac,
        ip: device.ip_address || device.ip || device.ipAddress || 'N/A',
        serialNumber: device.serial_number || device.serialNumber || device.sn || 'N/A',
        tags: (() => {
          const t = device.tags;
          if (!t) return [];
          if (Array.isArray(t)) return t.filter(Boolean).map(String);
          if (typeof t === 'object') {
            return Object.entries(t)
              .filter(([key, value]) => Boolean(value))
              .map(([key]) => String(key));
          }
          return [String(t)];
        })(),
        hashrate: hashrateDisplay,
        temperature: v3Temps,
        fan: v3Fans,
        worker: getWorkerFromPools(device.pools),
        client: device.owner_name || device.owner?.name || 'N/A',
        owner: (device.owner_id || device.owner_name)
          ? { id: device.owner_id || device.owner?.id, name: device.owner_name || device.owner?.name }
          : null,
        hosting: device.hosting_name || device.hosting || device.hostingName || undefined,
      };
    }

    const matchingData = dataDashboard?.find((data) => data.device_id === device.id);
    const normalizedStatus = normalizeStatusValue(device?.status);

    // Определяем хешрейт и метрики: берём из latest_metrics (новая структура devices)
    let hashrateDisplay = '-';
    const unit = device.hashrate_unit || device.hashrateUnit || 'TH/s';
    const { hashrate: v3Hashrate, temperatures: v3Temps, fans: v3Fans } = extractInstantDeviceMetricsFromV3(device);
    
    // Показываем хешрейт только для устройств со статусом Online
    if (normalizedStatus === 'online') {
      // Если не получили хешрейт из latest_metrics, пробуем взять последнюю точку из charts
      let latestRaw = v3Hashrate;
      if (latestRaw == null) {
        const series = matchingData?.hashrate;
        if (Array.isArray(series) && series.length > 0) {
        const last = series[series.length - 1];
        const raw = last?.hashrate;
          latestRaw = raw != null && isFinite(Number(raw)) ? Number(raw) : null;
        }
      }

      if (latestRaw != null) {
        const result = convertHashrate(latestRaw, unit, device.algorithm);
        hashrateDisplay = `${result.value.toFixed(2)} ${result.unit}`;
      }
    }

    return {
      model: device.model,
      modelName: device.model_name || null,
      status: normalizedStatus,
      algorithm: device.algorithm,
      mac: device.mac,
      ip: device.ip_address || device.ip || device.ipAddress || 'N/A',
      serialNumber: device.serial_number || device.serialNumber || device.sn || 'N/A',
      tags: (() => {
        const t = device.tags;
        if (!t) return [];
        if (Array.isArray(t)) return t.filter(Boolean).map(String);
        if (typeof t === 'object') {
          return Object.entries(t)
            .filter(([key, value]) => Boolean(value))
            .map(([key]) => String(key));
        }
        return [String(t)];
      })(),
      hashrate: hashrateDisplay,
      temperature: v3Temps, // Теперь берём только из latest_metrics (v3Temps)
      fan: v3Fans, // Теперь берём только из latest_metrics (v3Fans)
      worker: getWorkerFromPools(device.pools),
      client: device.owner_name || device.owner?.name || 'N/A',
      owner: (device.owner_id || device.owner_name)
        ? { id: device.owner_id || device.owner?.id, name: device.owner_name || device.owner?.name }
        : null,
      hosting: device.hosting_name || device.hosting || device.hostingName || undefined,
    };
  };

  // Функция для получения worker из пулов согласно логике приоритетов
  const getWorkerFromPools = (pools) => {
    if (!pools || !Array.isArray(pools) || pools.length === 0) {
      return 'N/A';
    }

    // 1. Ищем пул с active: true
    const activePool = pools.find(pool => pool.active === true);
    if (activePool && activePool.worker) {
      return activePool.worker;
    }

    // 2. Если нет активного пула, ищем пул с priority: null
    const nullPriorityPool = pools.find(pool => pool.priority === null);
    if (nullPriorityPool && nullPriorityPool.worker) {
      return nullPriorityPool.worker;
    }

    // 3. Если нет пула с priority: null, ищем пул с наименьшим priority
    const poolsWithPriority = pools.filter(pool => pool.priority !== null && pool.priority !== undefined);
    if (poolsWithPriority.length > 0) {
      const minPriorityPool = poolsWithPriority.reduce((min, pool) => 
        pool.priority < min.priority ? pool : min
      );
      if (minPriorityPool && minPriorityPool.worker) {
        return minPriorityPool.worker;
      }
    }

    // 4. В новой структуре может не быть active/priority — берем первый пул с worker
    const anyWorkerPool = pools.find((pool) => typeof pool.worker === 'string' && pool.worker.trim() !== '');
    if (anyWorkerPool) {
      return anyWorkerPool.worker;
    }

    // 5. Если ничего не найдено, возвращаем N/A
    return 'N/A';
  };

  const handleDeviceClick = (deviceId) => {
    navigate(`/device/${deviceId}`);
  };

  const handleOwnerClick = (ownerId) => {
    if (ownerId) {
      navigate(`/profile/${ownerId}`);
    }
  };

  const handleCreateLocation = async (parentId, locationName) => {
    try {
      // Здесь должен быть API вызов для создания новой локации
      
      // Пример API вызова:
      // const response = await axios.post(`${API_BASE_URL}/locations`, {
      //   parentId,
      //   name: locationName
      // });
      
      // После успешного создания обновляем данные
      // fetchDashboardData();
      
      // Показываем уведомление об успехе
      showSuccess(t('divisionCreated'));
    } catch (error) {
      showError(t('divisionCreateError'));
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      // Здесь должен быть API вызов для удаления локации
      
      // Пример API вызова:
      // const response = await axios.delete(`${API_BASE_URL}/locations/${locationId}`);
      
      // После успешного удаления обновляем данные
      // fetchDashboardData();
      
      // Показываем уведомление об успехе
      showSuccess(t('divisionDeleted'));
    } catch (error) {
      showError(t('divisionDeleteError'));
    }
  };

  // Обработчики для управления устройствами
  const handleDeviceRestart = async (deviceIds) => {
    try {
      // Здесь должен быть API вызов для перезапуска устройств
      // const response = await axios.post(`${API_BASE_URL}/devices/restart`, { deviceIds });
      
      showSuccess(t('restartStarted', { count: deviceIds.length }));
    } catch (error) {
      showError(t('restartError'));
    }
  };

  const handleDeviceReset = async (deviceIds) => {
    try {
      // Здесь должен быть API вызов для сброса устройств
      // const response = await axios.post(`${API_BASE_URL}/devices/reset`, { deviceIds });
      
      showSuccess(t('resetStarted', { count: deviceIds.length }));
    } catch (error) {
      showError(t('resetError'));
    }
  };

  const handleDeviceChangePool = async (deviceIds) => {
    try {
      // Здесь должен быть API вызов для смены пула
      // const response = await axios.post(`${API_BASE_URL}/devices/change-pool`, { deviceIds });
      
      showSuccess(t('poolChangeStarted', { count: deviceIds.length }));
    } catch (error) {
      showError(t('poolChangeError'));
    }
  };

  const handleDeviceRequestLogs = async (deviceIds) => {
    try {
      // Здесь должен быть API вызов для запроса логов
      // const response = await axios.post(`${API_BASE_URL}/devices/request-logs`, { deviceIds });
      
      showSuccess(t('logsRequested', { count: deviceIds.length }));
    } catch (error) {
      showError(t('logsRequestError'));
    }
  };

  // Callback для отслеживания открытия/закрытия Timeline
  const handleTimelineOpenChange = useCallback((isOpen) => {
    setIsTimelineOpen(isOpen);
  }, []);

  const deviceRows = dashboardData?.devices?.map((device) => ({
    ...createDeviceData(device, dataDashboard),
    id: device.id, // Добавляем id устройства для перехода
    alertRules: device.alert_rule_devices && device.alert_rule_devices.length > 0 ? device.alert_rule_devices[0] : null, // Добавляем правила алертов
  })) || [];

  // Динамические списки для фильтров
  const algorithms = React.useMemo(() => {
    const uniqueAlgorithms = new Set();
    deviceRows.forEach(row => {
      if (row.algorithm) {
        uniqueAlgorithms.add(row.algorithm);
      }
    });
    return Array.from(uniqueAlgorithms).sort();
  }, [deviceRows]);

  const hostingOptions = React.useMemo(() => {
    const uniqueHostings = new Set();
    deviceRows.forEach(row => {
      if (row.hosting) {
        uniqueHostings.add(row.hosting);
      }
    });
    return Array.from(uniqueHostings).sort();
  }, [deviceRows]);

  const tagOptions = React.useMemo(() => {
    const uniqueTags = new Set();
    deviceRows.forEach(row => {
      if (Array.isArray(row.tags)) {
        row.tags.forEach(tag => {
          if (tag) {
            uniqueTags.add(tag);
          }
        });
      }
    });
    return Array.from(uniqueTags).sort();
  }, [deviceRows]);

  const statusOptions = React.useMemo(() => {
    const uniqueStatuses = new Set();
    deviceRows.forEach(row => {
      if (row.status && row.status !== null) {
        uniqueStatuses.add(row.status);
      }
    });

    // Всегда включаем Online и Offline первыми
    const statuses = ['online', 'offline'];

    // Добавляем остальные уникальные статусы
    Array.from(uniqueStatuses).forEach(status => {
      const normalizedStatus = status.toLowerCase();
      if (normalizedStatus !== 'online' && normalizedStatus !== 'offline') {
        statuses.push(status);
      }
    });

    return statuses;
  }, [deviceRows]);

  // Функция для подсчета устройств для каждого значения фильтра
  // excludeFilter - фильтр, который нужно исключить из подсчета
  const getFilteredCountForOption = React.useCallback((optionValue, filterType, excludeFilter = null) => {
    return deviceRows.filter((row) => {
      const lowerCaseQuery = searchQuery.toLowerCase();

      const matchesSearch =
        row.mac?.toLowerCase().includes(lowerCaseQuery) ||
        row.model?.toLowerCase().includes(lowerCaseQuery) ||
        row.serialNumber?.toLowerCase().includes(lowerCaseQuery) ||
        row.ip?.toLowerCase().includes(lowerCaseQuery) ||
        row.client?.toLowerCase().includes(lowerCaseQuery) ||
        row.worker?.toLowerCase().includes(lowerCaseQuery);

      const matchesAlgorithm = excludeFilter === 'algorithm'
        ? row.algorithm === optionValue
        : (selectedAlgorithms.length === 0 || selectedAlgorithms.includes(row.algorithm));

      const matchesHosting = excludeFilter === 'hosting'
        ? row.hosting === optionValue
        : (selectedHosting.length === 0 || selectedHosting.includes(row.hosting));

      const matchesStatus = excludeFilter === 'status'
        ? row.status?.toLowerCase() === optionValue.toLowerCase()
        : (selectedStatus.length === 0 || selectedStatus.some(status => {
            const normalizedRowStatus = row.status?.toLowerCase();
            const normalizedSelectedStatus = status.toLowerCase();
            return normalizedRowStatus === normalizedSelectedStatus;
          }));

      const matchesTags = excludeFilter === 'tags'
        ? row.tags?.includes(optionValue)
        : (selectedTags.length === 0 || selectedTags.some(tag => row.tags?.includes(tag)));

      return matchesSearch && matchesAlgorithm && matchesHosting && matchesStatus && matchesTags;
    }).length;
  }, [deviceRows, searchQuery, selectedAlgorithms, selectedHosting, selectedStatus, selectedTags]);

  // Опции с подсчетом для каждого фильтра
  const algorithmsWithCount = React.useMemo(() => {
    return algorithms.map(algo => ({
      value: algo,
      label: algo,
      count: getFilteredCountForOption(algo, 'algorithm', 'algorithm')
    }));
  }, [algorithms, getFilteredCountForOption]);

  const hostingOptionsWithCount = React.useMemo(() => {
    return hostingOptions.map(hosting => ({
      value: hosting,
      label: hosting,
      count: getFilteredCountForOption(hosting, 'hosting', 'hosting')
    }));
  }, [hostingOptions, getFilteredCountForOption]);

  const statusOptionsWithCount = React.useMemo(() => {
    return statusOptions.map(status => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
      count: getFilteredCountForOption(status, 'status', 'status')
    }));
  }, [statusOptions, getFilteredCountForOption]);

  const tagOptionsWithCount = React.useMemo(() => {
    return tagOptions.map(tag => ({
      value: tag,
      label: tag,
      count: getFilteredCountForOption(tag, 'tags', 'tags')
    }));
  }, [tagOptions, getFilteredCountForOption]);

  const handleFilterFocus = React.useCallback((filterName) => {
    setExpandedFilter(filterName);
  }, []);

  const handleFilterBlur = React.useCallback(() => {
    setExpandedFilter(null);
  }, []);

  const chipSx = {
    maxWidth: 140,
    '& .MuiChip-label': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };

  const renderFilterTags = React.useCallback((filterName) => (value, getTagProps) => {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    const formatLabel = (option) => {
      const rawLabel = typeof option === 'string'
        ? option
        : option?.label || option?.name || '';
      if (rawLabel && rawLabel.length > 30) {
        return `${rawLabel.slice(0, 27)}…`;
      }
      return rawLabel;
    };

    if (expandedFilter !== filterName && value.length > 1) {
      return (
        <Chip
          {...getTagProps({ index: 0 })}
          label={`${value.length}+`}
          size="small"
          sx={chipSx}
        />
      );
    }

    return value.map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={index}
        label={formatLabel(option) || '—'}
        size="small"
        sx={chipSx}
      />
    ));
  }, [expandedFilter]);

  // на мобилке фильтры по 2 в ряд
  const getFilterStyle = React.useCallback((filterName) => ({
    flex: isMobile ? '1 1 calc(50% - 5px)' : (expandedFilter === filterName ? 2 : 1),
    transition: 'flex 0.2s ease',
    minWidth: isMobile ? 'auto' : (expandedFilter === filterName ? 180 : 120),
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
      alignItems: 'center',
    },
    '& .MuiAutocomplete-tag': {
      maxHeight: 28,
    },
    '& .MuiAutocomplete-input': {
      minWidth: 0,
    },
  }), [expandedFilter, isMobile]);

  const filteredRows = deviceRows.filter((row) => {
    const lowerCaseQuery = searchQuery.toLowerCase();

    const matchesSearch =
      row.mac?.toLowerCase().includes(lowerCaseQuery) ||
      row.model?.toLowerCase().includes(lowerCaseQuery) ||
      row.serialNumber?.toLowerCase().includes(lowerCaseQuery) ||
      row.ip?.toLowerCase().includes(lowerCaseQuery) ||
      row.client?.toLowerCase().includes(lowerCaseQuery) ||
      row.worker?.toLowerCase().includes(lowerCaseQuery);

    const matchesAlgorithm =
      selectedAlgorithms.length === 0 || selectedAlgorithms.includes(row.algorithm);

    const matchesHosting =
      selectedHosting.length === 0 || selectedHosting.includes(row.hosting);

    const matchesStatus =
      selectedStatus.length === 0 ||
      selectedStatus.some(status => {
        const normalizedRowStatus = row.status?.toLowerCase();
        const normalizedSelectedStatus = status.toLowerCase();
        return normalizedRowStatus === normalizedSelectedStatus;
      });

    const matchesTags =
      selectedTags.length === 0 || selectedTags.some(tag => row.tags?.includes(tag));

    // Скрываем устройства со статусом "delete" и "removed", если эти статусы не выбраны явно в фильтре
    const isDeleteStatus = row.status?.toLowerCase() === 'delete';
    const isRemovedStatus = row.status?.toLowerCase() === 'removed';
    const isDeleteExplicitlySelected = selectedStatus.some(status => status.toLowerCase() === 'delete');
    const isRemovedExplicitlySelected = selectedStatus.some(status => status.toLowerCase() === 'removed');
    const shouldHideDelete = isDeleteStatus && !isDeleteExplicitlySelected;
    const shouldHideRemoved = isRemovedStatus && !isRemovedExplicitlySelected;

    return matchesSearch && matchesAlgorithm && matchesHosting && matchesStatus && matchesTags && !shouldHideDelete && !shouldHideRemoved;
  });

  // Сортировка отфильтрованных строк
  const sortedRowsBeforePagination = React.useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Обработка статуса
      if (sortConfig.key === 'status') {
        // Приоритет: online > other > offline > null
        const getStatusPriority = (status) => {
          if (status === 'online') return 3;
          if (status === 'offline') return 1;
          if (status === null) return 0;
          return 2; // other statuses
        };
        aValue = getStatusPriority(aValue);
        bValue = getStatusPriority(bValue);
      }

      // Обработка IP адресов
      if (sortConfig.key === 'ip') {
        const result = compareIPAddresses(aValue, bValue);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      // Обработка uptime
      if (sortConfig.key === 'uptime') {
        const aUptime = calculateDeviceUptime(a.id);
        const bUptime = calculateDeviceUptime(b.id);
        if (sortConfig.direction === 'asc') {
          return aUptime - bUptime;
        } else {
          return bUptime - aUptime;
        }
      }

      // Обработка hashrate
      if (sortConfig.key === 'hashrate') {
        // Извлекаем числовое значение из строки вида "15.23 TH/s"
        const parseHashrate = (hashrateStr) => {
          if (!hashrateStr || hashrateStr === '-' || hashrateStr === 'N/A') return 0;
          const match = hashrateStr.match(/^([\d.]+)\s*([A-Z]+\/s)$/);
          if (!match) return 0;

          const value = parseFloat(match[1]);
          const unit = match[2];

          // Конвертируем все в H/s для корректного сравнения
          const multipliers = {
            'H/s': 1,
            'KH/s': 1000,
            'MH/s': 1000000,
            'GH/s': 1000000000,
            'TH/s': 1000000000000,
            'PH/s': 1000000000000000,
            'EH/s': 1000000000000000000,
          };

          return value * (multipliers[unit] || 1);
        };

        const aHashrate = parseHashrate(aValue);
        const bHashrate = parseHashrate(bValue);

        if (sortConfig.direction === 'asc') {
          return aHashrate - bHashrate;
        } else {
          return bHashrate - aHashrate;
        }
      }

      // Обработка текстовых полей
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredRows, sortConfig]);

  // Применяем пагинацию к отсортированным строкам
  const sortedRows = React.useMemo(() => {
    // В режиме дерева всегда показываем все устройства (без пагинации)
    if (isTreeView || pagination.rowsPerPage === 0) {
      return sortedRowsBeforePagination;
    }

    const startIndex = pagination.page * pagination.rowsPerPage;
    const endIndex = startIndex + pagination.rowsPerPage;
    return sortedRowsBeforePagination.slice(startIndex, endIndex);
  }, [sortedRowsBeforePagination, pagination, isTreeView]);

  // Общее количество страниц
  const totalPages = React.useMemo(() => {
    if (pagination.rowsPerPage === 0) return 1;
    return Math.ceil(sortedRowsBeforePagination.length / pagination.rowsPerPage);
  }, [sortedRowsBeforePagination.length, pagination.rowsPerPage]);

  // Обработчик изменения страницы
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Используем только выделенные через галочки устройства (или все, если ничего не выделено)
  // НО скрываем устройства со статусом "delete" и "removed", если эти статусы не выбраны явно в фильтре
  const filteredDeviceRows = (() => {
    // Определяем базовый набор устройств на основе выделения галочками
    // Сводка считается по тому, что показано в таблице (фильтры). Галочки на неё не влияют — они для команд.
    const baseRows = filteredRows;

    // Проверяем, выбраны ли статусы "delete" и "removed" явно в фильтре
    const isDeleteExplicitlySelected = selectedStatus.some(status => status.toLowerCase() === 'delete');
    const isRemovedExplicitlySelected = selectedStatus.some(status => status.toLowerCase() === 'removed');

    // Фильтруем устройства на основе выбранных статусов
    return baseRows.filter(row => {
      const status = row.status?.toLowerCase();

      // Показываем устройство со статусом "delete" только если он выбран явно
      if (status === 'delete' && !isDeleteExplicitlySelected) {
        return false;
      }

      // Показываем устройство со статусом "removed" только если он выбран явно
      if (status === 'removed' && !isRemovedExplicitlySelected) {
        return false;
      }

      return true;
    });
  })();

  const deviceByIdMap = useMemo(() => {
    const map = new Map();
    (deviceRows || []).forEach((row) => {
      if (row?.id) {
        map.set(row.id, row);
      }
    });
    return map;
  }, [deviceRows]);

  const buildLocationTree = useCallback((locations, devices, deviceStatusMap) => {
    
    const root = { 
      id: 'root', 
      name: 'root', 
      children: {}, 
      devices: [], 
      totalDevices: 0, 
      hasOnline: false, 
      hasOffline: false 
    };

    // Вспомогательная функция для создания узла
    const createNode = (key, name) => ({
      id: key,
      name: name || t('noName'),
      children: {},
      devices: [],
      totalDevices: 0,
      hasOnline: false,
      hasOffline: false,
    });

    // Создаем карту имен хостингов из устройств (fallback)
    const hostingNamesFromDevices = {};
    devices.forEach(device => {
      const hostingId = device.hosting_id;
      const hostingName = device.hosting_name || device.hosting || device.hostingName;
      if (hostingId && hostingName && !hostingNamesFromDevices[hostingId]) {
        hostingNamesFromDevices[hostingId] = hostingName;
      }
    });

    // Шаг 1: Строим дерево из локаций (по hosting_id)
    // Группируем локации по hosting_id
    const locationsByHosting = {};
    locations.forEach(location => {
      const hostingId = location.hosting_id;
      if (!locationsByHosting[hostingId]) {
        locationsByHosting[hostingId] = [];
      }
      locationsByHosting[hostingId].push(location);
    });
    
    // Создаем карту имен хостингов из устройств (fallback)
    Object.entries(locationsByHosting).forEach(([hostingId, hostingLocations]) => {
      // Получаем имя хостинга (приоритет: из локации, из устройств, UUID)
      const hostingName = hostingLocations[0]?.hosting_name 
        || hostingNamesFromDevices[hostingId] 
        || `Hosting ${hostingId}`;
      const hostingKey = `root/hosting:${hostingId}`;
      
      // Создаем узел хостинга
      if (!root.children[hostingKey]) {
        root.children[hostingKey] = createNode(hostingKey, hostingName);
      }
      const hostingNode = root.children[hostingKey];

      // Создаем карту локаций для быстрого доступа
      const locationMap = new Map();
      hostingLocations.forEach(loc => {
        const locKey = `${hostingKey}/loc:${loc.id}`;
        locationMap.set(loc.id, {
          key: locKey,
          location: loc,
          node: createNode(locKey, loc.name)
        });
      });

      // Строим иерархию локаций
      hostingLocations.forEach(location => {
        const locInfo = locationMap.get(location.id);
        if (!locInfo) return;

        if (!location.parent_id || location.is_root) {
          // Корневая локация - добавляем к хостингу
          hostingNode.children[locInfo.key] = locInfo.node;
        } else {
          // Дочерняя локация - добавляем к родителю
          const parentInfo = locationMap.get(location.parent_id);
          if (parentInfo) {
            parentInfo.node.children[locInfo.key] = locInfo.node;
          } else {
            hostingNode.children[locInfo.key] = locInfo.node;
          }
        }
      });
    });

    // Шаг 2: Добавляем устройства к построенным узлам
    const noLocationKey = 'no-location';
    
    (devices || []).forEach((device) => {
      if (!device?.id) return;

      // Определяем хостинг устройства
      const hostingName = device.hosting_name || device.hosting || device.hostingName || t('noHosting');
      const hostingId = device.hosting_id;
      
      if (!hostingId) {
        // Устройство без хостинга - пропускаем или добавляем в специальную секцию
        return;
      }
      
      const hostingKey = `root/hosting:${hostingId}`;
      
      // Создаем узел хостинга если его еще нет
      if (!root.children[hostingKey]) {
        root.children[hostingKey] = createNode(hostingKey, hostingName);
      }

      // Определяем location_id устройства
      const deviceLocationId = device.location_id || device.location?.id;
      
      if (!deviceLocationId) {
        // Устройство без локации - добавляем в "Без локации" под хостингом
        const noLocKey = `${hostingKey}/${noLocationKey}`;
        if (!root.children[hostingKey].children[noLocKey]) {
          root.children[hostingKey].children[noLocKey] = createNode(noLocKey, t('noLocation'));
        }
        
        const status = deviceStatusMap.get(device.id);
        const isOnline = status === 'online';
        const isOffline = status === 'offline';
        
        root.children[hostingKey].children[noLocKey].devices.push({ id: device.id, isOnline, isOffline });
        root.children[hostingKey].children[noLocKey].hasOnline = root.children[hostingKey].children[noLocKey].hasOnline || isOnline;
        root.children[hostingKey].children[noLocKey].hasOffline = root.children[hostingKey].children[noLocKey].hasOffline || isOffline;
        return;
      }

      // Ищем узел локации для устройства
      const locKey = `${hostingKey}/loc:${deviceLocationId}`;
      const findNode = (node) => {
        if (node.id === locKey) return node;
        for (const child of Object.values(node.children)) {
          const found = findNode(child);
          if (found) return found;
        }
        return null;
      };

      const targetNode = findNode(root.children[hostingKey]);
      if (targetNode) {
        const status = deviceStatusMap.get(device.id);
        const isOnline = status === 'online';
        const isOffline = status === 'offline';
        
        targetNode.devices.push({ id: device.id, isOnline, isOffline });
        targetNode.hasOnline = targetNode.hasOnline || isOnline;
        targetNode.hasOffline = targetNode.hasOffline || isOffline;
      } else {
        // Локация не найдена - добавляем в "Без локации"
        const noLocKey = `${hostingKey}/${noLocationKey}`;
        if (!root.children[hostingKey].children[noLocKey]) {
          root.children[hostingKey].children[noLocKey] = createNode(noLocKey, t('noLocation'));
        }
        
        const status = deviceStatusMap.get(device.id);
        const isOnline = status === 'online';
        const isOffline = status === 'offline';
        
        root.children[hostingKey].children[noLocKey].devices.push({ id: device.id, isOnline, isOffline });
        root.children[hostingKey].children[noLocKey].hasOnline = root.children[hostingKey].children[noLocKey].hasOnline || isOnline;
        root.children[hostingKey].children[noLocKey].hasOffline = root.children[hostingKey].children[noLocKey].hasOffline || isOffline;
      }
    });

    // Шаг 3: Подсчитываем общие метрики
    const attachTotals = (node) => {
      const own = Array.isArray(node.devices) ? node.devices.length : 0;
      const childKeys = Object.keys(node.children || {});
      const childrenTotal = childKeys.reduce((sum, key) => sum + attachTotals(node.children[key]), 0);
      node.totalDevices = own + childrenTotal;
      node.hasOnline = node.hasOnline || childKeys.some((key) => node.children[key].hasOnline);
      node.hasOffline = node.hasOffline || childKeys.some((key) => node.children[key].hasOffline);
      return node.totalDevices;
    };

    attachTotals(root);
    
    return root;
  }, []);

  const visibleDeviceIds = React.useMemo(() => new Set(sortedRows.map((row) => row.id)), [sortedRows]);

  const locationTree = React.useMemo(() => {
    const sourceDevices = (dashboardData?.devices || []).filter((device) => visibleDeviceIds.has(device.id));
    
    const statusMap = new Map();
    (sortedRows || []).forEach((row) => {
      statusMap.set(row.id, row.status);
    });
    return buildLocationTree(locationsData, sourceDevices, statusMap);
  }, [buildLocationTree, locationsData, dashboardData?.devices, sortedRows, visibleDeviceIds]);

  const combinedHashrateData = algorithms
    .map((algorithm) => {
      // Фильтруем устройства по алгоритму (учет онлайна ниже)
      const matchingDevices = filteredDeviceRows.filter((row) => row.algorithm === algorithm);
      // Онлайн считаем по статусу, а не по ненулевому хешрейту
      const onlineCount = matchingDevices.filter((row) => row.status === 'online').length;
      // Считаем устройства с дополнительными статусами (не online и не offline)
      const otherStatusCount = matchingDevices.filter((row) =>
        row.status !== 'online' && row.status !== 'offline' && row.status !== null
      ).length;

      // Для суммарного текущего хешрейта берем из latest_metrics (devices) или последнюю точку из charts.
      // Только устройства на связи: у пропавших latest_metrics хранит последнее значение до обрыва.
      const onlineDevicesData = matchingDevices
        .filter((row) => row.status === 'online')
        .map((rowDevice) => {
          const originalDevice = dashboardData?.devices?.find((d) => d.id === rowDevice.id);
          const unit = originalDevice?.hashrate_unit || originalDevice?.hashrateUnit || 'TH/s';
          
          // Приоритет: latest_metrics из devices
          const latestMetrics = originalDevice?.latest_metrics;
          if (latestMetrics?.hashrate != null && isFinite(Number(latestMetrics.hashrate))) {
            return convertHashrate(Number(latestMetrics.hashrate), unit, algorithm);
          }
          
          // Fallback: последняя точка из charts
          const deviceData = dataDashboard?.find((data) => data.device_id === rowDevice.id);
            const series = deviceData?.hashrate;
            const lastPoint = Array.isArray(series) && series.length > 0 ? series[series.length - 1] : null;
            const seriesRaw = lastPoint?.hashrate;
            const seriesNum = seriesRaw != null && isFinite(Number(seriesRaw)) ? Number(seriesRaw) : null;
          
          if (seriesNum != null) {
            return convertHashrate(seriesNum, unit, algorithm);
          }
          
          return { value: 0, unit: 'TH/s' };
        })
        .filter((v) => v.value > 0);

      // Суммируем значения в TH/s для корректного сравнения
      const totalTh = onlineDevicesData.reduce((sum, v) => {
        if (v.unit === 'GH/s') {
          return sum + v.value / 1000;
        } else if (v.unit === 'kSol/s') {
          return sum + v.value; // kSol/s примерно равно TH/s
        } else {
          return sum + v.value;
        }
      }, 0);

      // Определяем единицу отображения
      let displayUnit = 'TH/s';
      let displayValue = totalTh;
      
      if (algorithm === 'Equihash') {
        displayUnit = 'kSol/s';
        displayValue = totalTh;
      } else if (algorithm === 'Scrypt') {
        // Для Scrypt: если меньше 1000 GH/s, показываем в GH/s, иначе в TH/s
        const ghValue = totalTh * 1000; // конвертируем TH/s в GH/s
        if (ghValue < 1000) {
          displayUnit = 'GH/s';
          displayValue = ghValue;
        } else {
          displayUnit = 'TH/s';
          displayValue = totalTh;
        }
      } else {
        // Для остальных алгоритмов: если меньше 1000 GH/s, показываем в GH/s, иначе в TH/s
        const ghValue = totalTh * 1000; // конвертируем TH/s в GH/s
        if (ghValue < 1000) {
          displayUnit = 'GH/s';
          displayValue = ghValue;
        } else {
          displayUnit = 'TH/s';
          displayValue = totalTh;
        }
      }

      return {
        algorithm,
        hashrate: totalTh > 0 ? `${displayValue.toFixed(2)} ${displayUnit}` : 'N/A',
        hashrateTh: totalTh > 0 ? totalTh : null, // для сводки: единицы подбираются автоматически (TH/s → PH/s → EH/s)
        onlineDevices: onlineCount,
        totalDevices: matchingDevices.length,
        otherStatusDevices: otherStatusCount,
      };
    });

  const workerData = filteredDeviceRows.map((row) => {
    const originalDevice = dashboardData?.devices?.find(d => d.id === row.id);
    const latestMetrics = originalDevice?.latest_metrics;
    
    return {
      status: row.status,
      model: row.model,
      mac: row.mac,
      created_at: latestMetrics?.created_at || originalDevice?.updated_at || 'N/A',
    };
  });


  const graphData = algorithms
    .map((algorithm) => {
      const matchingDevices = dataDashboard?.filter((data) => {
        return filteredDeviceRows.some((row) => row.algorithm === algorithm && row.id === data.device_id);
      });

      // Проверяем, нужна ли нормализация (для диапазонов до 3 часов)
      const start = dateRange?.start ? (dateRange.start.toDate ? dateRange.start.toDate() : new Date(dateRange.start)) : null;
      const end = dateRange?.end ? (dateRange.end.toDate ? dateRange.end.toDate() : new Date(dateRange.end)) : null;
      const diffInHours = start && end ? (end - start) / (1000 * 60 * 60) : null;
      const shouldNormalize = diffInHours !== null && diffInHours <= 3;
      
      // Функция для округления временной метки к ближайшей минуте
      const roundToNearestMinute = (timestamp) => {
        const date = new Date(timestamp);
        const seconds = date.getSeconds();
        const rounded = new Date(date);
        rounded.setSeconds(0, 0);
        if (seconds >= 30) {
          rounded.setMinutes(rounded.getMinutes() + 1);
        }
        
        // Проверяем, что округленное время не превышает текущее время
        const now = Date.now();
        const roundedTime = rounded.getTime();
        if (roundedTime > now) {
          // Если округленное время в будущем, возвращаем текущую минуту
          rounded.setMinutes(rounded.getMinutes() - 1);
        }
        
        return rounded.getTime();
      };
      
      // Агрегация по временным меткам: суммируем хешрейт всех устройств одного алгоритма
      const aggregatedByTime = new Map();
      (matchingDevices || []).forEach((device) => {
        const originalDevice = dashboardData?.devices?.find((d) => d.id === device.device_id);
        const unit = originalDevice?.hashrate_unit || originalDevice?.hashrateUnit || 'TH/s';
        let hashrateArray = device.hashrate || [];
        
        // Для диапазонов до 3 часов добавляем синтетические данные для пропущенных минут
        if (shouldNormalize && hashrateArray.length > 0) {
          const normalizedData = [];
          const sortedHashrate = [...hashrateArray].sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          );
          
          // Получаем данные uptime для определения статуса устройства
          const deviceUptimeData = device.uptime || [];
          
          for (let i = 0; i < sortedHashrate.length; i++) {
            normalizedData.push(sortedHashrate[i]);
            
            // Проверяем, есть ли следующая точка
            if (i < sortedHashrate.length - 1) {
              const currentTime = roundToNearestMinute(new Date(sortedHashrate[i].created_at).getTime());
              const nextTime = roundToNearestMinute(new Date(sortedHashrate[i + 1].created_at).getTime());
              const diffInMinutes = (nextTime - currentTime) / (1000 * 60);
              
              // Если пропущена ровно 1 минута, добавляем синтетическую точку
              if (diffInMinutes === 2) {
                const syntheticTime = new Date(currentTime + 60000);
                
                // Проверяем статус устройства в это время
                // Считаем unavailable: offline (false/"offline") или любой дополнительный статус (repair, maintenance и т.д.)
                const wasOffline = deviceUptimeData.some(uptimeRecord => {
                  const recordStart = new Date(uptimeRecord.started_at);
                  const recordEnd = uptimeRecord.ended_at ? new Date(uptimeRecord.ended_at) : new Date();

                  // Unavailable если:
                  // 1. Статус offline (для обратной совместимости с boolean false)
                  // 2. Статус "offline" (строка)
                  // 3. Любой дополнительный статус (repair, maintenance и т.д.) - все кроме "online"
                  const isUnavailable = uptimeRecord.status === false ||
                                       uptimeRecord.status === 'offline' ||
                                       (uptimeRecord.status !== 'online' && uptimeRecord.status !== true);

                  return isUnavailable &&
                         syntheticTime >= recordStart &&
                         syntheticTime <= recordEnd;
                });
                
                normalizedData.push({
                  created_at: syntheticTime.toISOString(),
                  hashrate: wasOffline ? 0 : sortedHashrate[i].hashrate,
                  isSynthetic: true,
                  isOffline: wasOffline
                });
              }
            }
          }
          
          hashrateArray = normalizedData;
        }
        
        hashrateArray.forEach((hash) => {
          const ts = new Date(hash.created_at);
          // Используем нормализованную временную метку для диапазонов до 3 часов
          const key = shouldNormalize ? roundToNearestMinute(ts.getTime()) : ts.getTime();
          const raw = hash?.hashrate;
          const numeric = raw != null && isFinite(Number(raw)) ? Number(raw) : null;
          
          let adjustedNumeric = numeric;
          
          // Для диапазонов больше 3 часов корректируем хешрейт с учетом времени простоя
          if (!shouldNormalize && numeric != null && numeric > 0 && device.uptime && hashrateArray.length > 1) {
            // Определяем временной интервал для этого агрегата
            // Для агрегатов больше 3 часов используем интервал между соседними точками
            const currentIndex = hashrateArray.findIndex(h => h.created_at === hash.created_at);
            let intervalStart, intervalEnd;
            
            if (currentIndex > 0 && currentIndex < hashrateArray.length - 1) {
              // Для средних точек используем интервал от предыдущей точки до текущей
              intervalStart = new Date(hashrateArray[currentIndex - 1].created_at);
              intervalEnd = new Date(hash.created_at);
            } else if (currentIndex === 0 && hashrateArray.length > 1) {
              // Для первой точки используем интервал до следующей точки
              intervalStart = new Date(hash.created_at);
              intervalEnd = new Date(hashrateArray[currentIndex + 1].created_at);
            } else if (currentIndex === hashrateArray.length - 1 && currentIndex > 0) {
              // Для последней точки используем интервал от предыдущей точки
              intervalStart = new Date(hashrateArray[currentIndex - 1].created_at);
              intervalEnd = new Date(hash.created_at);
            } else {
              // Если только одна точка или индекс не найден, пропускаем корректировку
              intervalStart = null;
              intervalEnd = null;
            }
            
            // Рассчитываем коэффициент доступности только если интервал определен
            if (intervalStart && intervalEnd) {
              const availabilityRatio = calculateAvailabilityRatio(device.uptime, intervalStart, intervalEnd);
              
              // Корректируем хешрейт с учетом коэффициента доступности
              adjustedNumeric = numeric * availabilityRatio;
            }
          }
          
          const result = adjustedNumeric != null ? convertHashrate(adjustedNumeric, unit, algorithm) : { value: 0, unit: 'TH/s' };
          // Конвертируем в TH/s для корректного суммирования
          let th = result.value;
          if (result.unit === 'GH/s') {
            th = result.value / 1000;
          } else if (result.unit === 'kSol/s') {
            th = result.value; // kSol/s примерно равно TH/s
          }
          const entry = aggregatedByTime.get(key) || { time: new Date(key), sum: 0, valuesCount: 0, nullCount: 0, availabilityRatios: [] };
          if (adjustedNumeric != null && th > 0) {
            entry.sum += th;
            entry.valuesCount += 1;
            // Сохраняем коэффициент доступности для расчета среднего
            if (!shouldNormalize && numeric != null && numeric > 0 && device.uptime && hashrateArray.length > 1) {
              const currentIndex = hashrateArray.findIndex(h => h.created_at === hash.created_at);
              let intervalStart, intervalEnd;
              
              if (currentIndex > 0 && currentIndex < hashrateArray.length - 1) {
                intervalStart = new Date(hashrateArray[currentIndex - 1].created_at);
                intervalEnd = new Date(hash.created_at);
              } else if (currentIndex === 0 && hashrateArray.length > 1) {
                intervalStart = new Date(hash.created_at);
                intervalEnd = new Date(hashrateArray[currentIndex + 1].created_at);
              } else if (currentIndex === hashrateArray.length - 1 && currentIndex > 0) {
                intervalStart = new Date(hashrateArray[currentIndex - 1].created_at);
                intervalEnd = new Date(hash.created_at);
              } else {
                intervalStart = null;
                intervalEnd = null;
              }
              
              if (intervalStart && intervalEnd) {
                const availabilityRatio = calculateAvailabilityRatio(device.uptime, intervalStart, intervalEnd);
                entry.availabilityRatios.push(availabilityRatio);
              }
            }
          } else if (raw == null) {
            entry.nullCount += 1;
          }
          aggregatedByTime.set(key, entry);
        });
      });

      let aggregatedData = Array.from(aggregatedByTime.values())
        .map((entry) => {
          // Рассчитываем средний коэффициент доступности
          const averageAvailabilityRatio = entry.availabilityRatios && entry.availabilityRatios.length > 0
            ? entry.availabilityRatios.reduce((sum, ratio) => sum + ratio, 0) / entry.availabilityRatios.length
            : 1;
          
          return {
            time: entry.time,
            hashrate: entry.valuesCount > 0 ? entry.sum : 0,
            isNull: entry.valuesCount === 0 && entry.nullCount > 0,
            deviceCount: entry.valuesCount,
            script: algorithm,
            // Добавляем информацию о среднем коэффициенте доступности для этого временного интервала
            averageAvailabilityRatio: averageAvailabilityRatio,
          };
        })
        .sort((a, b) => a.time - b.time);

      // Больше не обрезаем данные по краям - отображаем все данные как есть

      return { algorithm, data: aggregatedData };
    });

  React.useEffect(() => {
    const savedLayout = localStorage.getItem('dashboardLayout');
    if (savedLayout) {
      setBaseLayout(JSON.parse(savedLayout));
    }
    setTimeout(() => {
      updateComponentSizes(); // Обновляем размеры контейнеров после загрузки макета
    }, 300); // Даем немного времени перед обновлением
  }, []);

  React.useEffect(() => {
    const savedColumns = localStorage.getItem('dashboard-table-v2');
    if (savedColumns) {
      try {
        const parsed = JSON.parse(savedColumns);
        // Проверяем новый формат (с visible и order) или старый формат (только visible)
        if (parsed.visible) {
          setVisibleColumns((prev) => ({ ...prev, ...parsed.visible }));
          if (parsed.order && Array.isArray(parsed.order)) {
            // Добавляем новые колонки, которых нет в сохраненном порядке
            const savedOrder = parsed.order;
            const missingColumns = defaultColumnOrder.filter(col => !savedOrder.includes(col));
            setColumnOrder([...savedOrder, ...missingColumns]);
          }
        } else {
          // Старый формат - только видимость колонок
          setVisibleColumns((prev) => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        // Ошибка парсинга dashboard-table из localStorage
      }
    }

    // Загружаем сохраненные фильтры
    const savedFilters = localStorage.getItem('dashboard-filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        if (parsed.searchQuery !== undefined) setSearchQuery(parsed.searchQuery);
        if (parsed.selectedAlgorithms) setSelectedAlgorithms(parsed.selectedAlgorithms);
        if (parsed.selectedHosting) setSelectedHosting(parsed.selectedHosting);
        if (parsed.selectedStatus) setSelectedStatus(parsed.selectedStatus);
        if (parsed.selectedTags) setSelectedTags(parsed.selectedTags);
        if (parsed.sortConfig) setSortConfig(parsed.sortConfig);
        if (parsed.pagination) setPagination(parsed.pagination);
        if (parsed.isTreeView !== undefined) setIsTreeView(parsed.isTreeView);
      } catch (e) {
        // Ошибка парсинга dashboard-filters из localStorage
      }
    }
    // Устанавливаем флаг после загрузки фильтров
    setIsFiltersLoaded(true);
  }, []);

  // Сохраняем фильтры при их изменении (только после загрузки из localStorage)
  React.useEffect(() => {
    // Не сохраняем, пока не загрузили фильтры из localStorage
    if (!isFiltersLoaded) return;

    const filtersToSave = {
      searchQuery,
      selectedAlgorithms,
      selectedHosting,
      selectedStatus,
      selectedTags,
      sortConfig,
      pagination,
      isTreeView,
    };
    localStorage.setItem('dashboard-filters', JSON.stringify(filtersToSave));
  }, [searchQuery, selectedAlgorithms, selectedHosting, selectedStatus, selectedTags, sortConfig, pagination, isTreeView, isFiltersLoaded]);

  React.useEffect(() => {
    updateComponentSizes(); // Обновляем размеры контейнеров при изменении макета
  }, [layouts]);

  React.useLayoutEffect(() => {
    updateComponentSizes(); // Гарантированное обновление размеров контейнеров после рендеринга
  }, [layouts]);

  const updateComponentSizes = () => {
    const event = new Event('resize');
    window.dispatchEvent(event);
  };

  const handleResize = (layout, oldItem, newItem) => {
    if (!isEditing) return; // Блокируем изменение размера, если режим редактирования выключен

    if (newItem.i === 'a') {
      const container = document.getElementById('heatmap-container');
      if (container) {
        const event = new Event('resize');
        window.dispatchEvent(event);
      }
    }
  };

  const handleResizeStop = (newLayout) => {
    if (!isEditing) return; // Блокируем изменение размера, если режим редактирования выключен

    setBaseLayout(newLayout); // Обновляем состояние макета
    setTimeout(() => {
      updateComponentSizes(); // Обновляем размеры контейнеров
    }, 200);
  };

  const handleDragStop = (newLayout) => {
    if (!isEditing) return; // Блокируем перемещение, если режим редактирования выключен

    setBaseLayout(newLayout); // Обновляем состояние макета
    updateComponentSizes(); // Обновляем размеры контейнеров
  };

  // сбрасываем редактирование при переходе на мобилку (drag-drop на тач неудобен)
  React.useEffect(() => {
    if (isMobile && isEditing) setIsEditing(false);
  }, [isMobile]);

  const handleSwitchChange = (event) => {
    const isEditingMode = event.target.checked;
    setIsEditing(isEditingMode);
  };

  const saveLayout = () => {
    localStorage.setItem('dashboardLayout', JSON.stringify(baseLayout)); // Сохраняем макет в localStorage
    setIsEditing(false); // Выключаем режим редактирования
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      // Выбираем все видимые устройства (после фильтрации и сортировки)
      setSelectedDeviceIds(sortedRows.map(row => row.id));
    } else {
      setSelectedDeviceIds([]);
    }
  };

  // Сбрасываем selectAll и страницу пагинации при изменении фильтров
  React.useEffect(() => {
    setSelectAll(false);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, [searchQuery, selectedAlgorithms, selectedHosting, selectedStatus, selectedTags]);

  const handleRowSelect = (deviceId) => {
    setSelectedDeviceIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(deviceId)) {
        return prevSelectedIds.filter((id) => id !== deviceId);
      } else {
        return [...prevSelectedIds, deviceId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedDeviceIds([]);
    setSelectAll(false);
  };

  // Функция для проверки, есть ли активные фильтры (только фильтры, не сортировка/пагинация)
  const hasActiveFilters = () => {
    return (
      searchQuery !== '' ||
      selectedAlgorithms.length > 0 ||
      selectedHosting.length > 0 ||
      selectedStatus.length > 0 ||
      selectedTags.length > 0
    );
  };

  // Функция для сброса всех фильтров и настроек
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedAlgorithms([]);
    setSelectedHosting([]);
    setSelectedStatus([]);
    setSelectedTags([]);
    setSortConfig({ key: 'ip', direction: 'asc' });
    setPagination({ page: 0, rowsPerPage: 100 }); // По умолчанию 100
    localStorage.removeItem('dashboard-filters');
    showInfo(t('filtersReset'));
  };

  const getStatusStyle = (status) => {
    // Нормализуем статус для сравнения
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : status;

    if (normalizedStatus === 'online') {
      return {
        backgroundColor: '#4caf50', // Зеленый для Online
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else if (normalizedStatus === 'offline') {
      return {
        backgroundColor: '#f44336', // Красный для Offline
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else if (normalizedStatus === null || normalizedStatus === undefined) {
      return {
        backgroundColor: '#9e9e9e', // Серый для null/undefined
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else {
      return {
        backgroundColor: '#2196f3', // Синий для других статусов (repair, maintenance и т.д.)
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    }
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleColumnToggle = (column) => {
    setVisibleColumns((prev) => {
      const updatedColumns = {
        ...prev,
        [column]: !prev[column],
      };
      // Сохраняем в localStorage вместе с порядком
      localStorage.setItem('dashboard-table-v2', JSON.stringify({
        visible: updatedColumns,
        order: columnOrder
      }));
      // Сбрасываем layout доски при изменении видимости графика
      if (column === 'hashrateGraph') {
        localStorage.removeItem('dashboardLayout');
        setBaseLayout([
          { w: 3, h: 6, x: 3, y: 0, i: 'workers' },
          { w: 3, h: 6, x: 0, y: 0, i: 'uptime' },
          { w: 6, h: 6, x: 6, y: 0, i: 'graph' },
          { w: 12, h: 4, x: 0, y: 6, i: 'hashrate' },
        ]);
      }
      return updatedColumns;
    });
  };

  // Обработчики drag-and-drop для колонок
  const handleDragStart = (e, column) => {
    setDraggedColumn(column);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', column);
  };

  const handleDragOver = (e, column) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (column !== draggedColumn) {
      setDragOverColumn(column);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumn) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    setColumnOrder((prev) => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumn);

      // Удаляем перетаскиваемый элемент
      newOrder.splice(draggedIndex, 1);
      // Вставляем на новую позицию
      newOrder.splice(targetIndex, 0, draggedColumn);

      // Сохраняем в localStorage
      localStorage.setItem('dashboard-table-v2', JSON.stringify({
        visible: visibleColumns,
        order: newOrder
      }));

      return newOrder;
    });

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Получение читаемого названия колонки
  const getColumnLabel = (column) => {
    const labels = {
      mac: 'MAC',
      ip: t('columnIp'),
      serialNumber: t('columnSerial'),
      hashrate: 'Hashrate',
      fan: 'Fan',
      client: t('columnClient'),
      worker: 'Worker',
      temperature: t('columnTemp'),
      algorithm: t('columnAlgorithm'),
      status: t('columnStatus'),
      model: t('columnDevice'),
      modelName: t('columnModel'),
      tags: t('columnTags'),
      uptime: 'Uptime',
      hashrateGraph: t('columnHashrateGraph')
    };
    return labels[column] || column.charAt(0).toUpperCase() + column.slice(1);
  };

  const containerStyle = {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  };

  // Диалог диапазона дат
  const handleDateRangeDialogOpen = () => setOpenDateRangeDialog(true);
  const handleDateRangeDialogClose = () => setOpenDateRangeDialog(false);
  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };
  const handleQuickDateRange = (type) => {
    const now = dayjs();
    let start, end = now;
    
    switch (type) {
      case '1h':
        start = now.subtract(1, 'hour');
        break;
      case '3h':
        start = now.subtract(3, 'hour');
        break;
      case '6h':
        start = now.subtract(6, 'hour');
        break;
      case '24h':
        start = now.subtract(24, 'hour');
        break;
      case '3d':
        start = now.subtract(3, 'day');
        break;
      case '30d':
        start = now.subtract(30, 'day');
        break;
      case '90d':
        start = now.subtract(90, 'day');
        break;
      case '180d':
        start = now.subtract(180, 'day');
        break;
      default:
        return;
    }
    
    setDateRange({ start, end });
  };
  const handleDateRangeApply = () => {
    // Валидация максимального диапазона в 31 день
    if (dateRange.start && dateRange.end) {
      const diffInDays = dateRange.end.diff(dateRange.start, 'day', true);
      
      if (diffInDays > 31) {
        showWarning(t('maxRangeWarning'));
        const newStart = dateRange.end.subtract(31, 'day');
        setDateRange({ start: newStart, end: dateRange.end });
        return;
      }
      
      if (diffInDays < 0) {
        showError(t('startAfterEnd'));
        return;
      }
    }
    
    setPeriodPreset('custom');
    setRangeNonce((n) => n + 1);
    setOpenDateRangeDialog(false);
  };
  const formatDateRange = () => {
    if (!dateRange.start || !dateRange.end) return t('selectRange');
    const start = dateRange.start;
    const end = dateRange.end;
    const now = dayjs();

    // Вычисляем разницу
    const diffInHours = end.diff(start, 'hour', true);
    const diffInDays = end.diff(start, 'day', true);

    // Проверяем предустановленные диапазоны (с допуском ±5 минут)
    if (Math.abs(diffInHours - 1) < 0.1) return t('range1h');
    if (Math.abs(diffInHours - 3) < 0.1) return t('range3h');
    if (Math.abs(diffInHours - 6) < 0.1) return t('range6h');
    if (Math.abs(diffInHours - 24) < 0.1) return t('range24h');
    if (Math.abs(diffInDays - 3) < 0.02) return t('range3d');
    if (Math.abs(diffInDays - 30) < 0.02) return t('range30d');
    if (Math.abs(diffInDays - 90) < 0.02) return t('range90d');
    if (Math.abs(diffInDays - 180) < 0.02) return t('range180d');

    // Для кастомных диапазонов показываем даты
    if (diffInDays < 1) {
      // Меньше суток - показываем часы
      return t('rangeCustom', { start: start.format('DD.MM HH:mm'), end: end.format('HH:mm') });
    } else if (diffInDays <= 7) {
      // До недели - показываем даты с временем
      return t('rangeCustom', { start: start.format('DD.MM HH:mm'), end: end.format('DD.MM HH:mm') });
    } else {
      // Больше недели - показываем только даты
      return t('rangeCustomDays', { start: start.format('DD.MM'), end: end.format('DD.MM'), days: Math.round(diffInDays) });
    }
  };

  // Загрузка данных из кеша при инициализации
  useEffect(() => {
    const loadInitialData = async () => {
      // Пробуем загрузить из кеша
      const cachedDevices = deviceCache.get();
      
      if (cachedDevices && cachedDevices.length > 0) {
        // Устанавливаем кешированные данные для быстрого отображения
        setDashboardData({
          devices: cachedDevices,
          user: null, // Данные пользователя загрузим из API
        });
        setIsLoadingFromCache(false);
      } else {
        setIsLoadingFromCache(false);
      }
      
      // Затем загружаем актуальные данные из API
      await fetchDashboardData();
    };
    
    loadInitialData();
  }, []);

  // Перезагрузка данных при переключении архивных устройств
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchDashboardData();
  }, [showArchived]);

  useRefetchOnHostingChange(fetchDashboardData);

  // Мощность, профиль прошивки, кВт·ч и сумма за период (work_hours/summary). Нет данных — плитки покажут прочерк.
  const fetchWorkSummary = useCallback(async () => {
    try {
      const start = dateRange?.start ? dateRange.start : dayjs().subtract(1, 'day');
      const end = dateRange?.end ? dateRange.end : dayjs();
      const resp = await api.get(`${API_BASE_URL}work_hours/summary`, {
        params: { start_date: start.toISOString(), end_date: end.toISOString() },
      });
      setWorkSummary(resp?.data || null);
    } catch (e) {
      setWorkSummary(null);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchWorkSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeNonce]);
  useRefetchOnHostingChange(fetchWorkSummary);

  // После смены периода состояние уже новое — перезагружаем данные под него
  useEffect(() => {
    if (rangeNonce > 0) fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeNonce]);

  const handlePeriodPreset = (preset) => {
    if (preset === 'custom') {
      setOpenDateRangeDialog(true);
      return;
    }
    const cfg = PERIOD_PRESETS[preset];
    if (!cfg) return;
    setPeriodPreset(preset);
    try { localStorage.setItem('dashboard-period', preset); } catch (e) { /* хранилище недоступно */ }
    setDateRange({ start: dayjs().subtract(cfg[0], cfg[1]), end: dayjs() });
    setRangeNonce((n) => n + 1);
  };

  // Автоматическое обновление данных по интервалу
  useEffect(() => {
    if (refreshInterval <= 0) return; // Если интервал 0, отключаем автообновление

    const intervalId = setInterval(() => {
      fetchDashboardData(true); // true = автообновление, сдвигаем временное окно
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshInterval, fetchDashboardData]);





  // Функция для принудительного обновления данных
  const handleManualRefresh = () => {
    fetchDashboardData();
  };

  // Функция для изменения интервала обновления
  const handleSetRefreshInterval = (newInterval) => {
    setRefreshInterval(newInterval);
  };

  // Объект с функциями для передачи в Menu
  const refreshFunctions = {
    refreshData: handleManualRefresh,
    setRefreshInterval: handleSetRefreshInterval
  };

  // Проверяем, должна ли показываться расширенная функциональность (только для admin и manager)
  const shouldShowAdvancedFeatures = () => {
    return isHosterOrAbove(user?.role);
  };

  // Обработчик изменения типа отображения
  // TODO: Вернуть обработчик выбора типа отображения после восстановления UI
  // const handleDisplayTypeChange = (event) => {
  //   const newType = event.target.value;
  //   setDisplayType(newType);
  //   console.log('Выбран тип отображения:', newType);
  //   // Здесь можно добавить логику для изменения отображения дашборда
  // };


  return (
    <div style={containerStyle}>
      <Menu 
        style={{ position: 'relative', zIndex: 10 }} 
        onRefresh={refreshFunctions} 
        refreshInterval={refreshInterval}
        hideRefresh
      />
      <Container 
        maxWidth={false}
        style={{ 
          backgroundColor: theme.palette.background.default, 
          padding: '0px',
          position: 'relative',
          maxWidth: 1760
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '16px 20px 12px', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={periodPreset}
            onChange={(e, v) => v && handlePeriodPreset(v)}
          >
            <ToggleButton value="1h" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange1h')}</ToggleButton>
            <ToggleButton value="24h" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange24h')}</ToggleButton>
            <ToggleButton value="3d" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange3d')}</ToggleButton>
            <ToggleButton value="30d" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange30d')}</ToggleButton>
            <ToggleButton value="custom" sx={{ textTransform: 'none', px: 1.5 }} onClick={() => periodPreset === 'custom' && setOpenDateRangeDialog(true)}>
              <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.75 }} />
              {/* подпись своего периода без слова «Диапазон» */}
              {periodPreset === 'custom' ? formatDateRange().replace(/^[^:：]*[:：]\s*/, '') : t('summaryCustomPeriod')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 36, fontSize: 13, color: 'text.secondary', lineHeight: 1 }}>
              {lastUpdated ? (
                <>
                  {t('summaryUpdated')}&nbsp;
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {dayjs(lastUpdated).format('HH:mm:ss')}
                  </Box>
                </>
              ) : t('summaryLoading')}
            </Box>
            <Select
              size="small"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              sx={{ height: 36, fontSize: 13, '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' } }}
              inputProps={{ 'aria-label': t('summaryAutoRefresh') }}
            >
              <MenuItem value={60000}>{t('summaryAuto', { value: t('summaryEvery1m') })}</MenuItem>
              <MenuItem value={180000}>{t('summaryAuto', { value: t('summaryEvery3m') })}</MenuItem>
              <MenuItem value={600000}>{t('summaryAuto', { value: t('summaryEvery10m') })}</MenuItem>
              <MenuItem value={1800000}>{t('summaryAuto', { value: t('summaryEvery30m') })}</MenuItem>
              <MenuItem value={3600000}>{t('summaryAuto', { value: t('summaryEvery1h') })}</MenuItem>
            </Select>
            <Tooltip title={t('summaryRefreshNow')}>
              <IconButton
                onClick={handleManualRefresh}
                aria-label={t('summaryRefreshNow')}
                sx={{ width: 36, height: 36, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        {(() => {
          const ids = new Set(filteredDeviceRows.map((row) => row.id));
          const summaryDevices = (dashboardData?.devices || [])
            .filter((d) => ids.has(d.id))
            .map((d) => ({
              id: d.id,
              status: normalizeStatusValue(d.status),
              algorithm: d.algorithm,
              hashrate: d.latest_metrics?.hashrate ?? null,
              hashrate_unit: d.hashrate_unit || null,
              ip: d.ip_address || null,
              model: d.model_name || d.model || null,
            }));
          const uptimeByDevice = Array.isArray(dataDashboard)
            ? dataDashboard.filter((item) => ids.has(item.device_id)).map((item) => (Array.isArray(item.uptime) ? item.uptime : []))
            : [];
          return (
            <SummaryPanel
              devices={summaryDevices}
              uptimeByDevice={uptimeByDevice}
              graphData={graphData}
              chartError={chartError}
              chartLoading={chartLoading}
              onChartRetry={() => fetchDashboardData()}
              hashrateByAlgorithm={combinedHashrateData}
              dateRange={dateRange}
              workSummary={workSummary}
              agentSilence={dataDashboard?.agentSilence}
              onDeviceClick={(id) => navigate(`/device/${id}`)}
            />
          );
        })()}
        {/* шрифт фильтров как в сводке и таблице */}
        <Box style={{ display: 'flex', gap: isMobile ? '10px' : '20px', marginTop: isMobile ? '10px' : '20px', marginBottom: '20px', padding: '0 20px', alignItems: 'center', flexWrap: 'wrap' }} sx={{ '& .MuiInputBase-input, & .MuiInputLabel-root, & .MuiAutocomplete-input, & .MuiChip-label, & .MuiButton-root, & .MuiToggleButton-root': { fontSize: 13 },
          // вырез рамки под подпись считается от шрифта рамки: тот же размер, иначе зазор
          '& .MuiOutlinedInput-notchedOutline': { fontSize: 13 } }}>
          <TextField
            label={t('searchLabel')}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: isMobile ? '1 1 100%' : 1, minWidth: isMobile ? 'auto' : undefined }}
          />
          <Autocomplete
            multiple
            options={algorithmsWithCount}
            value={selectedAlgorithms.map(algo => algorithmsWithCount.find(a => a.value === algo) || algo)}
            onChange={(event, newValue) => {
              const values = newValue.map(item => typeof item === 'string' ? item : item.value);
              setSelectedAlgorithms(values);
            }}
            disableCloseOnSelect
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.count > 0 ? `${option.label} (${option.count})` : option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const compareValue = typeof value === 'string' ? value : value.value;
              return optionValue === compareValue;
            }}
            onOpen={() => handleFilterFocus('algorithms')}
            onClose={handleFilterBlur}
            onFocus={() => handleFilterFocus('algorithms')}
            onBlur={handleFilterBlur}
            renderInput={(params) => <TextField {...params} size="small" label={t('algorithmLabel')} variant="outlined" />}
            sx={getFilterStyle('algorithms')}
            renderTags={renderFilterTags('algorithms')}
          />
          <Autocomplete
            multiple
            options={hostingOptionsWithCount}
            value={selectedHosting.map(hosting => hostingOptionsWithCount.find(h => h.value === hosting) || hosting)}
            onChange={(event, newValue) => {
              const values = newValue.map(item => typeof item === 'string' ? item : item.value);
              setSelectedHosting(values);
            }}
            disableCloseOnSelect
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.count > 0 ? `${option.label} (${option.count})` : option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const compareValue = typeof value === 'string' ? value : value.value;
              return optionValue === compareValue;
            }}
            onOpen={() => handleFilterFocus('hosting')}
            onClose={handleFilterBlur}
            onFocus={() => handleFilterFocus('hosting')}
            onBlur={handleFilterBlur}
            renderInput={(params) => <TextField {...params} size="small" label={t('hostingLabel')} variant="outlined" />}
            sx={getFilterStyle('hosting')}
            renderTags={renderFilterTags('hosting')}
          />
          <Autocomplete
            multiple
            options={statusOptionsWithCount}
            value={selectedStatus.map(status => statusOptionsWithCount.find(s => s.value === status) || status)}
            onChange={(event, newValue) => {
              const values = newValue.map(item => typeof item === 'string' ? item : item.value);
              setSelectedStatus(values);
            }}
            disableCloseOnSelect
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option.charAt(0).toUpperCase() + option.slice(1);
              return option.count > 0 ? `${option.label} (${option.count})` : option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const compareValue = typeof value === 'string' ? value : value.value;
              return optionValue === compareValue;
            }}
            onOpen={() => handleFilterFocus('status')}
            onClose={handleFilterBlur}
            onFocus={() => handleFilterFocus('status')}
            onBlur={handleFilterBlur}
            renderInput={(params) => <TextField {...params} size="small" label={t('statusLabel')} variant="outlined" />}
            sx={getFilterStyle('status')}
            renderTags={renderFilterTags('status')}
          />
          <Autocomplete
            multiple
            options={tagOptionsWithCount}
            value={selectedTags.map(tag => tagOptionsWithCount.find(t => t.value === tag) || tag)}
            onChange={(event, newValue) => {
              const values = newValue.map(item => typeof item === 'string' ? item : item.value);
              setSelectedTags(values);
            }}
            disableCloseOnSelect
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.count > 0 ? `${option.label} (${option.count})` : option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const compareValue = typeof value === 'string' ? value : value.value;
              return optionValue === compareValue;
            }}
            onOpen={() => handleFilterFocus('tags')}
            onClose={handleFilterBlur}
            onFocus={() => handleFilterFocus('tags')}
            onBlur={handleFilterBlur}
            renderInput={(params) => <TextField {...params} size="small" label={t('tagsLabel')} variant="outlined" />}
            sx={getFilterStyle('tags')}
            renderTags={renderFilterTags('tags')}
          />
          {isAdminOrSuperAdmin(user?.role) && (
            <Chip
              label={t('summaryArchive')}
              color={showArchived ? 'warning' : 'default'}
              variant={showArchived ? 'filled' : 'outlined'}
              onClick={() => setShowArchived((v) => !v)}
            />
          )}
          <ToggleButtonGroup size="small" exclusive value={isTreeView ? 'tree' : 'table'} onChange={(e, v) => v && setIsTreeView(v === 'tree')}>
            <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryViewTable')}</ToggleButton>
            <ToggleButton value="tree" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryViewTree')}</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" size="small" startIcon={<ViewColumnOutlinedIcon />} onClick={handleDialogOpen}>
            {t('summaryColumns')}
          </Button>
          {hasActiveFilters() && (
            <Tooltip title={t('resetFilters')}>
              <IconButton
                color="error"
                onClick={handleResetFilters}
                style={{ marginLeft: 'auto' }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ px: '20px', mt: '-10px', mb: '14px', fontSize: 12.5, color: 'text.secondary' }}>
          {t('summaryShown', { shown: filteredRows.length, total: deviceRows.length })}
        </Typography>

        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{t('displaySettingsTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
              {t('dragToReorder')}
            </Typography>

            {/* Секция для пагинации таблицы */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1.5 }}>
                {t('tablePagination')}
              </Typography>
              <Autocomplete
                options={[
                  { label: t('allItems'), value: 0 },
                  { label: '10', value: 10 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 },
                  { label: '250', value: 250 },
                  { label: '500', value: 500 },
                  { label: '1000', value: 1000 },
                ]}
                value={{ label: pagination.rowsPerPage === 0 ? t('allItems') : String(pagination.rowsPerPage), value: pagination.rowsPerPage }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setPagination({ page: 0, rowsPerPage: newValue.value });
                  }
                }}
                disableClearable
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} size="small" label={t('showPerPage')} variant="outlined" size="small" />}
                fullWidth
                disabled={isTreeView}
              />
              {isTreeView && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('treeShowsAll')}
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
              {t('tableColumns')}
            </Typography>

            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              {columnOrder.map((column, index) => (
                <ListItem
                  key={column}
                  draggable
                  onDragStart={(e) => handleDragStart(e, column)}
                  onDragOver={(e) => handleDragOver(e, column)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    borderBottom: index < columnOrder.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'grab',
                    transition: 'all 0.2s ease',
                    bgcolor: draggedColumn === column
                      ? 'action.selected'
                      : dragOverColumn === column
                        ? 'primary.light'
                        : 'transparent',
                    opacity: draggedColumn === column ? 0.5 : 1,
                    borderTop: dragOverColumn === column ? '2px solid' : 'none',
                    borderTopColor: 'primary.main',
                    '&:hover': { bgcolor: draggedColumn ? undefined : 'action.hover' },
                    '&:active': { cursor: 'grabbing' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, cursor: 'grab' }}>
                    <DragIndicatorIcon color={draggedColumn === column ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <Checkbox
                    checked={visibleColumns[column]}
                    onChange={() => handleColumnToggle(column)}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ListItemText
                    primary={getColumnLabel(column)}
                    sx={{ ml: 1 }}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>{t('common:close')}</Button>
          </DialogActions>
        </Dialog>
        {/* Диалог выбора диапазона дат */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Dialog open={openDateRangeDialog} onClose={handleDateRangeDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('dateRangeTitle')}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '16px' }}>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('1h')}>{t('btn1h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('3h')}>{t('btn3h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('6h')}>{t('btn6h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('24h')}>{t('btn24h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('3d')}>{t('btn3d')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('30d')}>{t('btn30d')}</Button>
                  <Button size="small" variant="outlined" disabled>{t('btn90d')}</Button>
                  <Button size="small" variant="outlined" disabled>{t('btn180d')}</Button>
                </div>
                <Typography variant="caption" color="text.secondary" style={{ textAlign: 'center', marginBottom: '8px' }}>
                  {t('maxRange')}
                </Typography>
                <div style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', padding: '16px', backgroundColor: theme.palette.background.paper }}>
                  <DatePicker
                    label={t('startDate')}
                    value={dateRange.start}
                    onChange={(v) => v && handleDateRangeChange('start', v)}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'medium' } }}
                  />
                  <div style={{ margin: '16px 0', textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">{t('to')}</Typography>
                  </div>
                  <DatePicker
                    label={t('endDate')}
                    value={dateRange.end}
                    onChange={(v) => v && handleDateRangeChange('end', v)}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'medium' } }}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDateRangeDialogClose}>{t('common:cancel')}</Button>
              <Button onClick={handleDateRangeApply} variant="contained" color="primary" disabled={!dateRange.start || !dateRange.end}>
                {t('common:apply')}
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>
        

        <div style={{ padding: '0 20px' }}>
          <Commands
            selectedRows={selectedDeviceIds}
            deviceIds={selectedDeviceIds}
            uptimeData={dataDashboard}
            devices={dashboardData?.devices || []}
            onDataRefresh={fetchDashboardData}
            onOpenMoveDialog={() => setOpenMoveDialog(true)}
            onTimelineOpenChange={handleTimelineOpenChange}
            onClearSelection={handleClearSelection}
          />
        </div>
        
        <div style={{ padding: '0 20px' }}>
          {isTreeView ? (
            <LocationTree
              tree={locationTree}
              deviceByIdMap={deviceByIdMap}
              onDeviceClick={handleDeviceClick}
              onOwnerClick={handleOwnerClick}
              getStatusStyle={getStatusStyle}
              onCreateLocation={handleCreateLocation}
              onDeleteLocation={handleDeleteLocation}
              canCreate={true}
              canDelete={true}
              selectedDeviceIds={selectedDeviceIds}
              onDeviceSelect={handleRowSelect}
              onDataUpdate={fetchDashboardData}
              onOpenMoveDialog={() => setOpenMoveDialog(true)}
              visibleColumns={visibleColumns}
              columnOrder={columnOrder}
              calculateDeviceUptime={calculateDeviceUptime}
              getUptimeColor={getUptimeColor}
            />
          ) : (
            <>
              <DeviceTable
                sortedRows={sortedRows}
                selectedDeviceIds={selectedDeviceIds}
                selectAll={selectAll}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                sortConfig={sortConfig}
                theme={theme}
                onSelectAll={handleSelectAll}
                onRowSelect={handleRowSelect}
                onSort={handleSort}
                onDeviceClick={handleDeviceClick}
                onOwnerClick={handleOwnerClick}
                getStatusStyle={getStatusStyle}
                calculateDeviceUptime={calculateDeviceUptime}
                getUptimeColor={getUptimeColor}
                dashboardDevices={dashboardData?.devices}
                dataDashboard={dataDashboard}
                isMobile={isMobile}
              />

              {/* Элементы пагинации - только для режима таблицы */}
              {!isTreeView && pagination.rowsPerPage > 0 && totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '20px',
                  marginTop: '20px',
                  padding: '10px'
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 0}
                  >
                    {t('prevPage')}
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    {t('pageOf', { page: pagination.page + 1, total: totalPages, count: sortedRowsBeforePagination.length })}
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= totalPages - 1}
                  >
                    {t('nextPage')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Диалог перемещения устройств */}
        <MoveDevicesDialog
          open={openMoveDialog}
          onClose={() => setOpenMoveDialog(false)}
          selectedDeviceIds={selectedDeviceIds}
          onDataUpdate={fetchDashboardData}
        />

        <NotificationContainer />
      </Container>
      </div>
    );
  };

export default Dashboard;
